// Lightweight parser and localStorage sync for booking details embedded in messages

import { createPersistableBookingData } from '../store/bookingStore';

// Expected message patterns produced by MessageSender.jsx:
// - "📅 Gewünschtes Datum: dd.MM.yyyy"
// - "⏰ Zeit: HH:MM - HH:MM (X Stunden)"
// - "⏰ Bevorzugte Zeit: HH:MM - HH:MM (X Stunden)\n⏰ Alternativ bin ich auch zeitlich flexibel und kann den ganzen Tag"
// - "⏰ Ich bin zeitlich flexibel und kann den ganzen Tag"

const DATE_REGEX = /📅\s*Gewünschtes\s*Datum:\s*(\d{2})\.(\d{2})\.(\d{4})/;
const TIME_RANGE_REGEX = /⏰\s*(?:Zeit|Bevorzugte Zeit):\s*(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/;
const FLEX_REGEX = /⏰\s*(?:Ich bin zeitlich flexibel|Alternativ bin ich auch zeitlich flexibel)/i;

/**
 * Parse a single message content for booking preferences
 * @param {string} content
 * @returns {{ dateISO?: string, startTime?: string, endTime?: string, isFlexible?: boolean } | null}
 */
export function parseBookingPreferences(content) {
    if (!content || typeof content !== 'string') return null;

    const result = {};

    const dateMatch = content.match(DATE_REGEX);
    if (dateMatch) {
        const [ , dd, mm, yyyy ] = dateMatch;
        // Convert dd.MM.yyyy to ISO date (yyyy-MM-dd) without timezone
        const iso = `${yyyy}-${mm}-${dd}`;
        // Validate by constructing a date
        const d = new Date(iso + 'T00:00:00');
        if (!isNaN(d.getTime())) {
            result.dateISO = iso;
        }
    }

    const timeMatch = content.match(TIME_RANGE_REGEX);
    if (timeMatch) {
        result.startTime = timeMatch[1];
        result.endTime = timeMatch[2] === '24:00' ? '24:00' : timeMatch[2];
    }

    if (FLEX_REGEX.test(content)) {
        result.isFlexible = true;
    }

    return Object.keys(result).length ? result : null;
}

/**
 * From an array of messages, find the most recent booking preferences
 * @param {Array<{ content?: string, createdAt?: string | number }>} messages
 * @returns {{ dateISO?: string, startTime?: string, endTime?: string, isFlexible?: boolean } | null}
 */
export function extractLatestBookingFromMessages(messages) {
    if (!Array.isArray(messages) || messages.length === 0) return null;

    // Iterate from newest to oldest and return first parse with at least date
    const sorted = [...messages].sort((a, b) => {
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        return tb - ta;
    });

    for (const msg of sorted) {
        const parsed = parseBookingPreferences(msg?.content || '');
        if (parsed && (parsed.dateISO || parsed.startTime || parsed.isFlexible)) {
            return parsed;
        }
    }
    return null;
}

const STORAGE_KEY_PREFIX = 'studioBookingData_';

/**
 * Merge parsed booking data into the studio's booking localStorage entry.
 * Only sets fields if they exist in parsed data; preserves existing data.
 * @param {string} studioId
 * @param {{ dateISO?: string, startTime?: string, endTime?: string, isFlexible?: boolean }} parsed
 */
export function mergeBookingIntoLocalStorage(studioId, parsed) {
    if (!studioId || !parsed) return;
    const storageKey = `${STORAGE_KEY_PREFIX}${studioId}`;

    const existingRaw = localStorage.getItem(storageKey);
    let existing = null;
    try {
        existing = existingRaw ? JSON.parse(existingRaw) : null;
    } catch (_) {
        existing = null;
    }

    // bookingStore saves { data, timestamp }
    const existingData = existing?.data || {};

    const updated = {
        ...existingData,
        studioId: studioId,
        date: parsed.dateISO ?? existingData.date ?? '',
        startTime: parsed.startTime ?? existingData.startTime ?? '',
        endTime: parsed.endTime ?? existingData.endTime ?? '',
        // we store flexibility as a hint; bookingStore doesn't have a dedicated field
        isFlexible: parsed.isFlexible ?? existingData.isFlexible,
    };

    const toStore = {
        data: createPersistableBookingData(updated),
        timestamp: Date.now(),
    };

    localStorage.setItem(storageKey, JSON.stringify(toStore));
}

/**
 * Convenience: extract latest booking info from messages and merge to storage
 * @param {string} studioId
 * @param {Array<{ content?: string, createdAt?: string | number }>} messages
 */
export function syncBookingFromMessages(studioId, messages) {
    const parsed = extractLatestBookingFromMessages(messages);
    if (parsed) {
        mergeBookingIntoLocalStorage(studioId, parsed);
    }
}

export default {
    parseBookingPreferences,
    extractLatestBookingFromMessages,
    mergeBookingIntoLocalStorage,
    syncBookingFromMessages,
};

